import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { setStoredToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, AlertTriangle } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = match[1];

    (async () => {
      try {
        console.log("[AuthCallback] exchanging session_id...");
        const res = await api.post("/auth/session", { session_id: sessionId });
        console.log("[AuthCallback] session OK, user:", res.data?.user?.email);

        if (!res.data?.user) {
          throw new Error("Respuesta sin usuario");
        }

        // Persist the session_token to localStorage so subsequent requests can
        // send it via `Authorization: Bearer` — resilient path when the
        // cross-origin Set-Cookie header is dropped by the ingress.
        if (res.data.session_token) {
          setStoredToken(res.data.session_token);
        }

        // 1) Set the user in context immediately (optimistic)
        setUser(res.data.user);

        // 2) Verify cookie made it back to the browser by round-tripping /auth/me.
        //    This guarantees Set-Cookie was accepted before we protect-navigate.
        try {
          const me = await api.get("/auth/me");
          console.log("[AuthCallback] /auth/me OK:", me.data?.email);
          setUser(me.data);
        } catch (verifyErr) {
          console.warn("[AuthCallback] /auth/me verify failed:", verifyErr.response?.status);
          // Don't fail — the setUser above already unlocked ProtectedRoute
        }

        // 3) Clear hash from URL and navigate through router
        navigate("/dashboard", { replace: true });
      } catch (e) {
        console.error("[AuthCallback] auth failed:", e?.response?.status, e?.response?.data);
        setError(e?.response?.data?.detail || e?.message || "Error de autenticación");
        // Give the user a chance to read the error, then send them home
        setTimeout(() => navigate("/login", { replace: true }), 2500);
      }
    })();
  }, [navigate, setUser, checkAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <p className="font-heading text-lg font-semibold text-slate-900">No se pudo iniciar sesión</p>
          <p className="text-sm text-slate-600 mt-2">{error}</p>
          <p className="text-xs text-slate-500 mt-4">Redirigiendo al login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex items-center gap-3 text-slate-700">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="font-body text-sm">Iniciando sesión...</span>
      </div>
    </div>
  );
}
